import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyResults, Header } from "../../../../common/components";
import { ISectionData } from "../../../sections/ISections";
import { GroupContext } from "../context/GroupContext";
import { Dimension } from "@models/IDimension";
import SectionsContainer from "../../../sections/Sections";
import useGetDimension from "@dimensions/hooks/useGetDimension";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useGetHeaderInfo from "../hooks/useGetHeaderInfo";
import GroupTagsDrawer from "@dimensions/group/components/group-tags-drawer";
import useContextUrl from "@common/hooks/useContextUrl";
import GroupRestrictionsDrawer from "../components/group-restrictions-drawer";
import GroupReferencesDrawer from "../components/group-references-drawer";
import GroupProposalsDrawer from "../components/group-proposals-drawer";
import { useGroupProposals } from "../hooks/useGroupProposals";

const GroupCalendar: React.FC = () => {
  const { origin, scenario, workspace, id } = useContextUrl();
  const { state } = useContext(GroupContext);
  const { adaptedHomeUrl } = useNavigationUrl();
  const [dimension] = useGetDimension(Dimension.group);
  const { t } = useTranslation();
  const [showTagsDrawer, setShowTagsDrawer] = useState(false);
  const [showRestrictionsDrawer, setShowRestrictionsDrawer] = useState(false);
  const [showReferenceDrawer, setShowReferenceDrawer] = useState(false);
  const proposals = useGroupProposals(state?.info?.info);

  const { header, loading, error, calendarData, setCalendar, groupInfo } = useGetHeaderInfo({
    onShowTagsDrawer: () => setShowTagsDrawer(true),
    onShowRestrictionsDrawer: () => setShowRestrictionsDrawer(true),
    onShowReferenceDrawer: () => setShowReferenceDrawer(true),
    proposalsAccess: proposals.access,
  });

  const setDataCalendar = (data: ISectionData, callback: () => void) => {
    setCalendar(data);
    callback && callback();
  };

  const showSkeleton = (loading || !calendarData || !header) && !error;

  return (
    <>
      {!error && (
        <Header
          isLoading={
            showSkeleton
              ? { topTitle: true, title: true, subTitle: true, moreDetails: true }
              : undefined
          }
          dimension={dimension.label}
          breadcrumbItems={[
            {
              path: adaptedHomeUrl(),
              title: t("dimension.group.breadcrumb.previous"),
            },
            {
              title: t("dimension.group.breadcrumb.current"),
            },
          ]}
          {...(header ?? {})}
        />
      )}

      {!!groupInfo && !showSkeleton && (
        <>
          <GroupTagsDrawer
            isOpen={showTagsDrawer}
            labels={groupInfo?.labels}
            modalityCode={groupInfo?.course?.curriculum?.program?.modality?.code}
            groupId={id}
            onClose={() => setShowTagsDrawer(false)}
          />

          <GroupRestrictionsDrawer
            scenario={scenario}
            origin={origin}
            id={id}
            isOpen={showRestrictionsDrawer}
            onClose={() => setShowRestrictionsDrawer(false)}
          />

          <GroupReferencesDrawer
            groupId={id}
            termCode={state?.info?.info?.term?.code}
            isOpen={showReferenceDrawer}
            courseType={groupInfo?.course?.courseType}
            corequisiteKeys={groupInfo?.course?.corequisiteKeys}
            references={groupInfo?.references ?? []}
            onClose={() => setShowReferenceDrawer(false)}
            scenario={scenario}
            origin={origin}
          />

          {!!proposals.drawerProps && <GroupProposalsDrawer {...proposals.drawerProps} />}
        </>
      )}

      {error && <EmptyResults dimension={t("dimension.group.title")} isCalendarView />}

      {!error && (
        <SectionsContainer
          isLoading={showSkeleton}
          config={{
            scenarioId: parseInt(scenario),
            originId: parseInt(origin),
            workspaceId: parseInt(workspace),
          }}
          defaultTerm={state?.info?.info?.term}
          data={calendarData}
          setDataGrid={setDataCalendar}
        />
      )}
    </>
  );
};

export default GroupCalendar;
